/**
 * Apply tenant policy to a Rainforest `allowed-methods` string (URL/embed config).
 * Strips method types disabled at the tenant level (Apple Pay, ACH). If the
 * result would be empty, falls back to CARD-only — card can't be disabled.
 */
export function resolvePatientFacingAllowedMethods(
  urlAllowedMethods: string | null | undefined,
  tenantAllowsApplePay: boolean,
  tenantAllowsAch: boolean = true,
): string | undefined {
  const trimmed = urlAllowedMethods?.trim();
  if (tenantAllowsApplePay !== false && tenantAllowsAch !== false) {
    return trimmed || undefined;
  }

  const defaultList = ["CARD", "ACH", "APPLE_PAY"];
  const parts = trimmed
    ? trimmed
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : defaultList;
  const filtered = parts.filter((p) => {
    const upper = p.toUpperCase();
    if (upper === "APPLE_PAY" && tenantAllowsApplePay === false) return false;
    if (upper === "ACH" && tenantAllowsAch === false) return false;
    return true;
  });
  if (filtered.length === 0) {
    return "CARD";
  }
  return filtered.join(",");
}

/**
 * Whether an `allowed-methods` string leaves nothing but a bank account (ACH).
 * Such a form never shows card, so it must start unsurcharged rather than wait
 * for a method switch that never comes.
 */
export function isBankOnly(allowedMethods: string | undefined): boolean {
  const methods = (allowedMethods ?? "")
    .split(",")
    .map((m) => m.trim().toUpperCase())
    .filter(Boolean);
  return methods.length > 0 && methods.every((m) => m === "ACH");
}

/**
 * Whether the patient payment-link page offers Apple Pay.
 *
 * Hidden only for links carrying an existing member's charges
 * (`hasRenewalCharges`). Those are renewal invoices: the patient is usually
 * replacing a card that just declined, and what they pay with becomes the
 * renewal card, so steer them to a card or bank account. New membership sales
 * and new payment plans keep Apple Pay. This is the payment-link half of the
 * exception to APPLE_PAY_RENEWAL_POLICY in decoda/services/membership.py; the
 * API also blocks Apple Pay on these payins.
 */
export function paymentLinkAllowsApplePay({
  tenantAllowsApplePay,
  hasRenewalCharges,
}: {
  tenantAllowsApplePay: boolean;
  hasRenewalCharges: boolean;
}): boolean {
  return tenantAllowsApplePay && !hasRenewalCharges;
}

/** Build the Rainforest `allowed-methods` string for the patient payment-link page. */
export function buildPaymentLinkAllowedMethods({
  tenantAllowsApplePay,
  tenantAllowsAch,
  hasRenewalCharges,
}: {
  tenantAllowsApplePay: boolean;
  tenantAllowsAch: boolean;
  hasRenewalCharges: boolean;
}): string {
  const methods = ["CARD"];
  if (tenantAllowsAch) methods.push("ACH");
  if (paymentLinkAllowsApplePay({ tenantAllowsApplePay, hasRenewalCharges })) {
    methods.push("APPLE_PAY");
  }
  return methods.join(",");
}

/** Single-use wallet-token method identifiers that can't back a recurring draw. */
const WALLET_METHODS = new Set(["APPLE_PAY", "GOOGLE_PAY"]);

/**
 * Remove wallet tokens (Apple/Google Pay) from an `allowed-methods` string.
 * Save-on-file flows use this to guarantee the stored method is re-chargeable,
 * regardless of what a caller requests. Falls back to CARD if nothing
 * re-chargeable remains.
 */
export function stripWalletMethods(allowedMethods: string): string {
  const filtered = allowedMethods
    .split(",")
    .map((m) => m.trim())
    .filter((m) => m && !WALLET_METHODS.has(m.toUpperCase()));
  return filtered.length ? filtered.join(",") : "CARD";
}

/** True when a saved payment method is a wallet token (Apple/Google Pay). */
export function isWalletPaymentMethod(
  paymentMethod: { type?: string | null } | null | undefined,
): boolean {
  return Boolean(
    paymentMethod?.type && WALLET_METHODS.has(paymentMethod.type.toUpperCase()),
  );
}

/**
 * True when the patient has a saved method other than a wallet token that
 * isn't expired and can run on the membership's merchant account — a card or
 * bank account staff can actually fall back to if an Apple Pay renewal
 * declines. `availableMerchantIds`: null = every merchant, [] = none, [...] =
 * only those. With no merchant known, only methods usable nowhere are excluded.
 */
export function hasNonWalletBackup<
  T extends {
    type?: string | null;
    isExpired?: boolean;
    availableMerchantIds?: string[] | null;
  },
>(paymentMethods: T[], merchantAccountId?: string | null): boolean {
  return paymentMethods.some((pm) => {
    if (isWalletPaymentMethod(pm) || pm.isExpired) return false;
    const merchants = pm.availableMerchantIds;
    if (merchants === null || merchants === undefined) return true;
    if (merchants.length === 0) return false;
    return !merchantAccountId || merchants.includes(merchantAccountId);
  });
}
